import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Checkbox from 'atoms/Checkbox';
import LoadingButton from 'atoms/LoadingButton';
import isEmpty from 'lodash/isEmpty';
import { observer } from 'mobx-react-lite';
import FormInputText from 'molecules/FormField/FormInputText';
import PasswordField from 'molecules/FormField/PasswordField';
import FormTitle from 'molecules/Registration/FormHeader';
import PasswordStrength from 'molecules/Registration/PasswordStrength';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useStore } from 'stores/store';
import { IAuthData, signIn, signUp } from 'utils/authUtils';
import { errorMessage, validateEmail, validatePassword } from 'utils/errorUtils';
import { IResourceData, ISponsorData } from 'utils/globalTypes';
import { navigateToUrl } from 'utils/navigateUtils';
import { Resources } from 'utils/resources';

import s from '../ConnectForm.module.scss';
import SignUpFormPartners from './SignUpFormPartners';

const EMAIL_EXISTS_ERR = 'UsernameExistsException';

interface FormValues {
  email: string;
  password: string;
}

const SignUpForm = ({
  email,
  sponsorId,
  onStateChange,
  onRedirect,
  sponsors,
  partners,
  requestPending,
  setRequestPending
}: {
  email?: string;
  sponsorId?: string;
  onRedirect?: (val: string) => void;
  sponsors?: ISponsorData[];
  partners?: IResourceData[];
  requestPending?: boolean;
  setRequestPending: (pending: boolean) => void;
  onStateChange: (
    state: string,
    data?: IAuthData,
    redirect?: boolean,
    queriesToOmit?: string[]
  ) => Promise<void>;
}) => {
  const store = useStore();
  const router = useRouter();
  const [step, setStep] = useState('email');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [checkboxError, setCheckboxError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    getValues,
    clearErrors,
    control,
    watch
  } = useForm<FormValues>({
    defaultValues: { email: email ?? '', password: '' }
  });

  useEffect(() => {
    clearErrors('email');
    clearErrors('password');

    if (step === 'email') {
      register('email', {
        required: 'You must enter a valid email',
        validate: (value) => {
          return !value || validateEmail(value as string) || 'Email is not valid';
        }
      });
    }
    if (step === 'password') {
      register('password', {
        required: 'You must enter a valid password',
        validate: (value) => {
          return !value || validatePassword(value as string) || 'Password is not valid';
        }
      });
    }
  }, [register, step, clearErrors]);

  const removeStepQuery = useCallback(async () => {
    const { step, ...routerQuery } = router.query;
    await router.replace({ query: { ...routerQuery } }, undefined, { shallow: true });
  }, [router]);

  useEffect(() => {
    (async () => {
      const isEmailValid = validateEmail(getValues('email'));
      if (router?.query?.step === 'password' && !isEmailValid) {
        await removeStepQuery();
        setStep('email');
      }
      if (router?.query?.step === 'password' && isEmailValid) {
        setStep('password');
      }
      if (!router?.query?.step) {
        setStep('email');
      }
    })();
  }, [removeStepQuery, getValues, router?.query?.step]);

  const onError = async (err: Error | string) => {
    console.warn('SignUpForm - onError', err);
    setRequestPending(false);
    if (err instanceof Error && err.name === EMAIL_EXISTS_ERR) {
      setStep('email');
      await removeStepQuery();
      setError('email', {
        type: EMAIL_EXISTS_ERR,
        message: errorMessage(err, 'Email Invalid')
      });
      return;
    }
    setError('password', {
      type: 'manual',
      message: errorMessage(err, 'Password Invalid')
    });
  };

  const onConfirmSignUp = async (state: string, data: IAuthData) => {
    if (data && 'userConfirmed' in data && data['userConfirmed']) {
      onSignIn(state, data);
    } else {
      onSuccess(state, data);
    }
  };

  const onSignIn = async (state: string, data: IAuthData) => {
    const email = data && 'email' in data && (data['email'] as string);
    const password = data && 'password' in data && (data['password'] as string);
    if (email && password) {
      signIn(email, password, onSignedIn, onError);
    } else {
      onSuccess(state, data);
    }
  };

  const onSignedIn = async (state: string, data: IAuthData) => {
    store.ProfileStore.sendVerifyEmail();
    onSuccess(state, data);
  };

  const onSuccess = async (state: string, data: IAuthData) => {
    await onStateChange(state, data, undefined, ['step']);
  };

  const onSignUp = async (data: FormValues) => {
    if (!isEmpty(errors)) return;

    const isEmailValid = validateEmail(data.email);

    if (!isEmailValid) {
      setError('email', {
        type: 'manual',
        message: 'You must enter a valid email'
      });
      return;
    }

    if (step === 'email' && (!agreeTerms || !agreePrivacy)) {
      setCheckboxError('You must agree to both Terms of Use and Privacy Policy to continue.');
      return;
    }

    setCheckboxError(null);

    if (isEmailValid && step === 'email') {
      await navigateToUrl(router?.pathname, {
        query: { ...router?.query, step: 'password' },
        options: { shallow: true }
      });
      setStep('password');
    }

    if (!validatePassword(data.password) && step === 'password') {
      setError('password', {
        type: 'manual',
        message: 'You must enter a valid password'
      });
      return;
    }

    if (step === 'email') {
      return;
    }

    setRequestPending(true);
    clearErrors();

    signUp(data.email, data.password, sponsorId, onConfirmSignUp, onError);
  };

  const sponsor = sponsorId
    ? sponsors?.filter((sponsor) => sponsor?.id == sponsorId)[0]
    : undefined;

  console.log('signUp', sponsorId, sponsor);

  return (
    <div className={s.ConnectForm}>
      <FormTitle
        title={
          step === 'password'
            ? Resources.REGISTRATION_ENTER_PASSWORD_TITLE
            : sponsorId
            ? Resources.REGISTRATION_ACTIVATE_USREM_TITLE
            : Resources.REGISTRATION_SIGN_UP_TITLE
        }
      />
      {step === 'email' && (
        <>
          {sponsor?.name && <Box sx={{ mb: 2 }}>{`Dear ${sponsor?.name} Investor,`}</Box>}
          <Box component="ul" sx={{ mb: 3 }}>
            <li className={s.ConnectForm__signUpTextItem}>
              <span>
                <strong>Get</strong> offers on your current real estate holdings
              </span>
            </li>
            <li className={s.ConnectForm__signUpTextItem}>
              <span>
                <strong>Get</strong> unique access to lucrative real estate deals
              </span>
            </li>
            <li className={s.ConnectForm__signUpTextItem}>
              <span>
                <strong>Buy and sell</strong> fractions of residential and commercial real estate
                properties, on your terms!
              </span>
            </li>
          </Box>
        </>
      )}
      <form id="signUp" className={s.ConnectForm__form} onSubmit={handleSubmit(onSignUp)}>
        {step === 'email' && (
          <div className={s.ConnectForm__field}>
            <FormInputText
              control={control}
              name="email"
              type="email"
              autoFocus={true}
              description="Email"
              defaultValue={email}
              disabled={requestPending}
              TextFieldProps={{
                ...(errors.email &&
                  errors.email.type === EMAIL_EXISTS_ERR && {
                    helperText: (
                      <Typography component="span">
                        Email address already exists. Would you like to{' '}
                        <Link
                          component="button"
                          color="inherit"
                          onClick={() => onRedirect && onRedirect('signIn')}
                        >
                          login
                        </Link>
                        ?
                      </Typography>
                    )
                  })
              }}
            />
          </div>
        )}
        {step === 'password' && (
          <>
            <div className={s.ConnectForm__hidden}>
              <FormInputText
                control={control}
                name="email"
                type="email"
                description="Email"
                defaultValue={email}
                disabled={true}
              />
            </div>
            <div className={s.ConnectForm__field}>
              <PasswordField
                autoFocus={true}
                name="password"
                control={control}
                description="Password"
                disabled={requestPending}
              />
            </div>
          </>
        )}
        {step === 'password' && <PasswordStrength password={watch('password', '')} />}
        {step === 'email' && (
          <>
            <Typography variant="caption" color="text.secondary" mt={1}>
              <Checkbox
                sx={{ padding: 0.75 }}
                checked={agreeTerms}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setAgreeTerms(event.target.checked)
                }
              />
              {'I agree to '}
              <Link
                href="https://www.usremsecurities.com/terms-of-use/"
                color="inherit"
                target="_blank"
              >
                {'USREM’s Terms of Use'}
              </Link>
            </Typography>
            <Typography variant="caption" color="text.secondary" mt={1}>
              <Checkbox
                sx={{ padding: 0.75 }}
                checked={agreePrivacy}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setAgreePrivacy(event.target.checked)
                }
              />
              {'I agree to '}
              <Link
                href="https://www.usremsecurities.com/privacy-policy/"
                color="inherit"
                target="_blank"
              >
                {'Privacy Policy'}
              </Link>
            </Typography>
            {checkboxError && (
              <Typography variant="caption" color="error" mt={1}>
                {checkboxError}
              </Typography>
            )}
          </>
        )}

        <LoadingButton
          loading={requestPending}
          className={s.ConnectForm__submit__btn}
          type="submit"
        >
          {step === 'password'
            ? Resources.REGISTRATION_ACTIVATE_TITLE
            : Resources.REGISTRATION_CONTINUE}
        </LoadingButton>
        <Divider />
        <Typography mb={2.5} variant="caption" color="text.secondary" mt={1}>
          {'Already have an account? '}
          <Link component="button" color="inherit" onClick={() => onStateChange('signIn')}>
            {'Log in'}
          </Link>
        </Typography>
        <SignUpFormPartners partners={partners} />
        {/*<GoogleSignIn*/}
        {/*  requestPending={requestPending}*/}
        {/*  setRequestPending={setRequestPending}*/}
        {/*  onStateChange={onStateChange}*/}
        {/*  onError={onError}*/}
        {/*/>*/}
      </form>
    </div>
  );
};

export default observer(SignUpForm);
